import { useNavigate } from "flemo";
import { CheckIcon, CrownIcon, LoaderIcon, RotateCcwIcon, SparklesIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useOfferingsQuery, usePurchaseMutation, useRestorePurchasesMutation } from "@/hooks/queries";
import { trackEvent } from "@/lib/analytics";
import { logger } from "@/lib/logger";
import { isNativeApp } from "@/lib/native";
import { fadeTransition } from "@/lib/transitions";
import { useConfettiStore } from "@/store/confettiStore";

const PAYWALL_REOPEN_KEY = "paywall_reopen";

const PLUS_FEATURES = [
  "전체 단어장 잠금 해제",
  "스마트 복습 (망각 곡선)",
  "상세 학습 분석",
  "오토 플레이",
  "과거 오답 노트 보기",
  "광고 없는 학습",
];

interface PaywallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
}

export function PaywallDialog({ open, onOpenChange, source = "unknown" }: PaywallDialogProps) {
  const { push } = useNavigate();
  const { data: offering, isPending: isOfferingPending } = useOfferingsQuery();
  const purchaseMutation = usePurchaseMutation();
  const restoreMutation = useRestorePurchasesMutation();
  const fireConfetti = useConfettiStore(state => state.fire);

  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [restoreMessage, setRestoreMessage] = useState<string | null>(null);

  const packages = offering?.packages ?? [];
  const monthlyPkg = packages.find(p => p.packageType === "MONTHLY");
  const threeMonthPkg = packages.find(p => p.packageType === "THREE_MONTH");
  const annualPkg = packages.find(p => p.packageType === "ANNUAL");

  // 월 환산 가격 및 할인율 계산
  const monthlyPrice = monthlyPkg?.product.price ?? 0;

  const threeMonthMonthlyPrice = threeMonthPkg ? threeMonthPkg.product.price / 3 : null;
  const threeMonthSavings =
    monthlyPrice && threeMonthMonthlyPrice ? Math.round((1 - threeMonthMonthlyPrice / monthlyPrice) * 100) : null;

  const annualMonthlyPrice = annualPkg ? annualPkg.product.price / 12 : null;
  const annualSavings =
    monthlyPrice && annualMonthlyPrice ? Math.round((1 - annualMonthlyPrice / monthlyPrice) * 100) : null;

  // 기본 선택: 월간 > 3개월 > 연간
  const effectiveSelection =
    selectedPackageId ?? monthlyPkg?.identifier ?? threeMonthPkg?.identifier ?? annualPkg?.identifier ?? null;

  const handlePurchaseSuccess = useCallback(() => {
    onOpenChange(false);
    fireConfetti();
  }, [onOpenChange, fireConfetti]);

  const handlePurchase = async () => {
    if (!effectiveSelection) {
      return;
    }

    setRestoreMessage(null);

    const selectedPkg = packages.find(p => p.identifier === effectiveSelection);
    const packageType = selectedPkg?.packageType ?? "UNKNOWN";

    trackEvent("purchase_initiated", {
      package_id: effectiveSelection,
      package_type: packageType,
      price: selectedPkg?.product.price,
      currency: selectedPkg?.product.currencyCode,
    });

    try {
      logger.info({ domain: "paywall", message: "구매 시작", data: { packageId: effectiveSelection } });
      const result = await purchaseMutation.mutateAsync(effectiveSelection);
      logger.info({ domain: "paywall", message: "구매 결과", data: { result } });

      if (result?.isPlus) {
        trackEvent("purchase_completed", {
          package_id: effectiveSelection,
          package_type: packageType,
          price: selectedPkg?.product.price,
          currency: selectedPkg?.product.currencyCode,
        });
        handlePurchaseSuccess();
      } else {
        logger.warn({ domain: "paywall", message: "구매 mutation 성공했으나 isPlus가 falsy", data: { result } });
      }
    } catch (error) {
      // 에러 원본은 native Sentry에서 캡처 — 웹은 breadcrumb만
      logger.warn({
        domain: "paywall",
        message: "구매 중 에러",
        data: { packageId: effectiveSelection, errorMessage: error instanceof Error ? error.message : String(error) },
      });
    }
  };

  const handleRestore = async () => {
    setRestoreMessage(null);
    trackEvent("restore_purchases_initiated");

    try {
      logger.info({ domain: "paywall", message: "복원 시작" });
      const result = await restoreMutation.mutateAsync();
      logger.info({ domain: "paywall", message: "복원 결과", data: { result } });

      if (result?.isPlus) {
        trackEvent("restore_purchases_completed", { restored: true });
        handlePurchaseSuccess();
      } else {
        trackEvent("restore_purchases_completed", { restored: false });
        setRestoreMessage("활성 구독을 찾을 수 없습니다. 구독했던 계정으로 로그인해 주세요.");
      }
    } catch (error) {
      // 에러 원본은 native Sentry에서 캡처 — 웹은 breadcrumb만
      logger.warn({
        domain: "paywall",
        message: "복원 중 에러",
        data: { errorMessage: error instanceof Error ? error.message : String(error) },
      });
    }
  };

  const isProcessing = purchaseMutation.isPending || restoreMutation.isPending;

  // 약관/개인정보 페이지에서 뒤로가기 시 Dialog 복원
  useEffect(() => {
    const handlePopState = () => {
      if (sessionStorage.getItem(PAYWALL_REOPEN_KEY)) {
        sessionStorage.removeItem(PAYWALL_REOPEN_KEY);
        onOpenChange(true);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, [onOpenChange]);

  // 페이월 열림 이벤트
  useEffect(() => {
    if (open) {
      trackEvent("paywall_opened", { source });
    }
  }, [open, source]);

  if (!isNativeApp) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm gap-6 p-6">
        <DialogHeader>
          <div className="mx-auto mb-1 flex size-10 items-center justify-center rounded-full bg-primary/10">
            <CrownIcon className="size-5 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl font-bold">TOBO Plus</DialogTitle>
          <DialogDescription className="text-center">더 효과적인 학습을 위한 프리미엄 기능</DialogDescription>
        </DialogHeader>

        {/* 혜택 목록 */}
        <ul className="space-y-2">
          {PLUS_FEATURES.map(feature => (
            <li key={feature} className="flex items-center gap-2 text-sm">
              <CheckIcon className="size-4 shrink-0 text-primary" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* 상품 선택 */}
        {isOfferingPending ? (
          <div className="flex justify-center py-4">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex gap-3">
            {monthlyPkg && (
              <PackageOption
                label="월간"
                subLabel={monthlyPkg.product.priceString + "/월"}
                selected={effectiveSelection === monthlyPkg.identifier}
                onSelect={() => setSelectedPackageId(monthlyPkg.identifier)}
              />
            )}
            {threeMonthPkg && (
              <PackageOption
                label="3개월"
                subLabel={threeMonthPkg.product.priceString}
                badge={threeMonthSavings ? `${threeMonthSavings}% 할인` : undefined}
                selected={effectiveSelection === threeMonthPkg.identifier}
                onSelect={() => setSelectedPackageId(threeMonthPkg.identifier)}
              />
            )}
            {annualPkg && (
              <PackageOption
                label="연간"
                subLabel={annualPkg.product.priceString + "/년"}
                badge={annualSavings ? `${annualSavings}% 할인` : undefined}
                selected={effectiveSelection === annualPkg.identifier}
                onSelect={() => setSelectedPackageId(annualPkg.identifier)}
              />
            )}
          </div>
        )}

        {/* 구매 버튼 */}
        <Button
          size="lg"
          className="w-full gap-1.5 py-5"
          disabled={!effectiveSelection || isProcessing}
          onClick={handlePurchase}
        >
          {purchaseMutation.isPending ? (
            <LoaderIcon className="size-4 animate-spin" />
          ) : (
            <SparklesIcon className="size-4" />
          )}
          {purchaseMutation.isPending ? "처리 중..." : "Plus 시작하기"}
        </Button>

        {/* 복원 버튼 */}
        <button
          type="button"
          className="flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          disabled={isProcessing}
          onClick={handleRestore}
        >
          {restoreMutation.isPending ? (
            <LoaderIcon className="size-3 animate-spin" />
          ) : (
            <RotateCcwIcon className="size-3" />
          )}
          구매 복원
        </button>

        {/* 부가세·청약철회 안내 */}
        <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
          모든 가격은 부가세(VAT)가 포함된 금액입니다. 구매일로부터 7일 이내 청약철회(환불)를 요청할 수 있으며, 자세한
          방법은 이용약관을 확인해 주세요.
        </p>

        {/* 구독 조건 안내 */}
        <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
          <button
            type="button"
            className="inline underline underline-offset-2 hover:text-foreground"
            onClick={() => {
              sessionStorage.setItem(PAYWALL_REOPEN_KEY, "true");
              onOpenChange(false);
              push("/terms", {}, { transitionName: fadeTransition.name });
            }}
          >
            이용약관
          </button>
          {" | "}
          <button
            type="button"
            className="inline underline underline-offset-2 hover:text-foreground"
            onClick={() => {
              sessionStorage.setItem(PAYWALL_REOPEN_KEY, "true");
              onOpenChange(false);
              push("/privacy", {}, { transitionName: fadeTransition.name });
            }}
          >
            개인정보처리방침
          </button>
        </p>

        {restoreMessage && <p className="text-center text-xs text-muted-foreground">{restoreMessage}</p>}

        {purchaseMutation.isError && (
          <p className="text-center text-xs text-destructive">구매에 실패했습니다. 다시 시도해 주세요.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- 내부 컴포넌트 ---------- */

function PackageOption({
  label,
  subLabel,
  badge,
  selected,
  onSelect,
}: {
  label: string;
  subLabel: string;
  badge?: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative flex flex-1 flex-col items-center gap-0.5 rounded-lg border-2 p-3 transition-colors ${
        selected ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
      }`}
    >
      {badge && (
        <span className="absolute -top-2.5 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
          {badge}
        </span>
      )}
      <span className="text-sm font-semibold">{label}</span>
      <span className="text-xs text-muted-foreground">{subLabel}</span>
    </button>
  );
}
